import axios, { AxiosError } from 'axios';
import { auth } from './firebase';
import * as Sentry from '@sentry/nextjs';
import { moengageLogout } from '@/lib/moengage';

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  headers: {
    key: process.env.NEXT_PUBLIC_SECRET_KEY,
    platform: "web",
  },
  timeout: 15000,
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    if (typeof window !== 'undefined') {
      // Ensure device ID exists
      let deviceId = localStorage.getItem('deviceId');
      if (!deviceId) {
        deviceId = `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('deviceId', deviceId);
      }
      config.headers['device-id'] = deviceId;

      // Get fresh Firebase token
      const user = auth?.currentUser;
      if (user) {
        const firebaseToken = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${firebaseToken}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Log the error to Sentry in production (if it's not a session auth error)
    // if (error.response?.status !== 401) {
    //   Sentry.captureException(error, {
    //     extra: {
    //       status: error.response?.status,
    //       url: error.config?.url,
    //       method: error.config?.method,
    //       data: error.response?.data,
    //     },
    //   });
    // }

    // if (error.response?.status === 500) {
    //   Sentry.captureException(error, {
    //     extra: {
    //       status: error.response?.status,
    //       url: error.config?.url,
    //       method: error.config?.method,
    //       data: error.response?.data,
    //     },
    //   });
    // }

    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const responseData = error.response?.data as any;

        // Only redirect if not already on auth pages
        if (!currentPath.startsWith('/auth/')) {
          moengageLogout();

          await auth?.signOut();

          let loginUrl = '/auth/login';
          if (responseData?.code) {
            loginUrl += `?error=${responseData.code}`;
          }
          window.location.href = loginUrl;
        }
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface Plan {
  _id: string;
  name: string;
  price: number;
  priceStrikeThrough: number;
  validity: number;
  validityType: string;
  planBenefit: string[];
  productKeys: {
    googlePlay: string;
    razorpay: string;
    cashfree?: string;
  };
  isAutoRenew: boolean;
  tag: string;
  productKey: string;
  freeTrialAmount: number;
}

export interface PremiumPlansResponse {
  status: boolean;
  message: string;
  premiumPlan: Plan[];
}

// Types for subscription
export interface CreateSubscriptionResponse {
  status: boolean;
  message: string;
  subscription?: {
    id: string;
    customerId: string;
    planId: string;
    status: string;
    shortUrl: string;
  };
  subscriptionId?: string;
  customerId?: string;
  orderId?: string;
  amount?: number;
}

// API functions
export const getPremiumPlans = async (): Promise<PremiumPlansResponse> => {
  const { data } = await api.get<PremiumPlansResponse>('/premiumPlan');
  return data;
};

export const createSubscription = async (premiumPlanId: string): Promise<CreateSubscriptionResponse> => {
  const deviceId = localStorage.getItem('deviceId') || `web-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const { data } = await api.post<CreateSubscriptionResponse>('/premiumPlan/razorpay/createSubscription', {
    premiumPlanId,
    deviceId,
    platform: "web"
  });
  return data;
};

export const verifySubscriptionPayment = async (paymentData: {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}) => {
  const { data } = await api.post('/premiumPlan/razorpay/verifyPayment', paymentData);
  return data;
};

export interface Banner {
  _id: string;
  image: string;
  type: 'auth' | 'subscription';
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BannersResponse {
  status: boolean;
  message: string;
  banners: Banner[];
}

export interface UserPlan {
  status?: string;
  planEndDate?: string;
  planStartDate?: string;
  historyId?: string;
  subscriptionDetails?: {
    paymentGateway?: string;
    amount?: number;
    isFreeTrial?: boolean;
    transactionId?: string;
  };
}

export interface User {
  _id: string;
  phoneNumber?: string;
  email?: string;
  fullName?: string;
  image?: string;
  campaignId?: string;
  appInstanceId?: string;
  isPremiumPlan: boolean;
  paymentProviderFreeTrialConsumed?: boolean;
  plan?: UserPlan;
  freeTrial?: any;
  isNewUser?: boolean;
}

export interface FirebaseLoginRequest {
  firebaseToken: string;
  deviceId: string;
  platform?: 'web' | 'android' | 'ios';
  adjustWebUUID?: string;
  adjustCampaignId?: string;
  couponCode?: string;
  sourcePath?: string;
  deviceInfo: {
    deviceName: string;
    deviceType: string;
    browserInfo: string;
  };
}

export interface FirebaseLoginResponse {
  status: boolean;
  message: string;
  data: {
    _id: string;
    coupon?: {
      status: boolean;
      valid?: boolean;
      message: string;
      code?: string;
      premiumplanId?: string;
      override?: any;
      source?: string;
      campaign?: string;
    };
    phoneNumber?: string;
    email?: string;
    fullName?: string;
    image?: string;
    campaignId?: string;
    appInstanceId?: string;
    isPremiumPlan: boolean;
    plan?: {
      status?: string;
      planEndDate?: string;
      planStartDate?: string;
      historyId?: string;
    };
    freeTrial?: any;
    isNewUser: boolean;
  };
}

export const firebaseLogin = async (payload: FirebaseLoginRequest): Promise<FirebaseLoginResponse> => {
  const { data } = await api.post<FirebaseLoginResponse>('/user/firebase-login', payload);
  return data;
};

export const getBanners = async (type?: 'auth' | 'subscription'): Promise<BannersResponse> => {
  const url = type ? `/banner/active/list?type=${type}` : '/banner/active/list';
  const { data } = await api.get<BannersResponse>(url);
  return data;
};

export interface OtplessLoginRequest {
  idToken: string;
  token: string;
  deviceId: string;
  appAdvertisingId?: string;
  platform?: 'web' | 'android' | 'ios';
  adjustWebUUID?: string;
  adjustCampaignId?: string;
  couponCode?: string;
  sourcePath?: string;
  deviceInfo?: {
    deviceName: string;
    deviceType: string;
    browserInfo: string;
  };
}

export type OtplessLoginResponse = Omit<FirebaseLoginResponse, 'data'> & {
  data: FirebaseLoginResponse['data'] & { customToken: string };
};

export const otplessLogin = async (payload: OtplessLoginRequest): Promise<OtplessLoginResponse> => {
  const { data } = await api.post<OtplessLoginResponse>('/user/otpless-login', payload);
  return data;
};

export interface CreateHistoryRequest {
  premiumPlanId: string;
  paymentGateway: string;
  paymentId: string;
  subscriptionId: string;
  customerId: string;
  planId: string;
}

export interface CreateHistoryResponse {
  status: boolean;
  message: string;
  history: {
    _id: string;
    isFreeTrial: boolean;
  }
}

export const createPremiumPlanHistory = async (payload: CreateHistoryRequest): Promise<CreateHistoryResponse> => {
  const { data } = await api.post<CreateHistoryResponse>('/premiumPlan/createHistory', payload);
  return data;
};

export interface SettingsResponse {
  status: boolean;
  message: string;
  setting: {
    isPaymentProviderFreeTrialEnabled: boolean;
    paymentProviderFreeTrialDays: number;
    paymentProviderFreeTrialText: string;
    adjustWebCampaignReference: Record<string, string>;
    loginScreenThumbnailImage?: string;
    loginScreenThumbnailVideo?: string;
  };
}

export interface UserProfileResponse {
  status: boolean;
  message: string;
  user: User;
}

export const getSettings = async (): Promise<SettingsResponse> => {
  const { data } = await api.get<SettingsResponse>('/setting');
  return data;
};

export const getUserProfile = async (): Promise<UserProfileResponse> => {
  const { data } = await api.get<UserProfileResponse>('/user/profile');
  return data;
};

// Custom Page Configuration Types
export interface CustomPageConfigResponse {
  status: boolean;
  message: string;
  data: {
    _id: string;
    mainHeading: string;
    secondaryHeading: string;
    footerText: string;
    disclaimerText: string;
    helpUrl: string;
    theme: {
      primaryColor: string;
      secondaryColor: string;
      backgroundColor: string;
      textColor: string;
      ctaTextColor: string;
      ctaBackgroundColor: string;
    };
    creative: {
      url: string;
      type: 'image' | 'video';
      thumbnailUrl?: string;
      order: number;
    }[];
    steps: {
      title: string;
      body: string;
      order: number;
      enabled: boolean;
    }[];
    socialLinks: {
      platform: string;
      url: string;
      iconUrl: string;
      order: number;
    }[];
    cta: {
      label: string;
      url: string;
      backgroundColor: string;
      textColor: string;
    };
    selectedPlanId?: string;
    planId?: string;
  };
}

export const getCustomPageConfig = async (): Promise<CustomPageConfigResponse> => {
  const { data } = await api.get<CustomPageConfigResponse>('/custom-page?type=subscription');
  return data;
};

// Cashfree Subscription Types
export interface CashfreeSubscriptionResponse {
  status: boolean;
  message: string;
  subscription?: {
    id: string;
    subReferenceId: string;
    customer_id: string;
    plan_id: string;
  };
  upiPaymentLinks?: {
    android: string;
    ios: string;
    web: string;
  };
}

export interface VerifyAuthPaymentResponse {
  status: boolean;
  message: string;
  subscriptionAuthStatus: 'SUCCESS' | 'PENDING' | 'FAILURE';
  failure_reason?: string;
  response_code?: string;
  paymentId?: string;
  customerId?: string;
}

// PhonePe Subscription Types
export interface PhonePeSubscriptionResponse {
  status: boolean;
  message: string;
  subscription?: {
    idForPolling?: string;
    subscriptionId?: string;
    orderId?: string;
  };
  upiPaymentLinks?: {
    android?: string;
    ios?: string;
    web?: string;
  };
}

export interface VerifyPhonePeSetupResponse {
  status: boolean;
  message: string;
  subscriptionAuthStatus: 'SUCCESS' | 'PENDING' | 'FAILURE';
  subscriptionId?: string;
  errorCode?: string;
}

export interface CancelPhonePeSubscriptionResponse {
  status: boolean;
  message: string;
  subscription?: {
    subscriptionId: string;
    status: string;
  };
}

// Cashfree API calls
export const createCashfreeSubscription = async (data: {
  premiumPlanId: string;
  upiApp?: string; // 'PHONEPE' | 'GPAY' | 'PAYTM' | 'DEFAULT'
  upiId?: string;
  couponCode?: string;
  sourcePath?: string;
}) => {
  const response = await api.post<CashfreeSubscriptionResponse>(
    'premiumPlan/cashfree/createSubscription',
    data
  );
  return response.data;
};

// PhonePe API calls
export const createPhonePeSubscription = async (data: {
  premiumPlanId: string;
  upiApp: string;
  upiPackageName: string;
  couponCode?: string;
  sourcePath?: string;
}) => {
  const response = await api.post<PhonePeSubscriptionResponse>(
    'premiumPlan/payment/createSubscription',
    {
      ...data,
      gateway: 'PhonePe',
    }
  );
  return response.data;
};

export const verifyPhonePeSubscription = async (idForPolling: string) => {
  const response = await api.get<VerifyPhonePeSetupResponse>(
    `premiumPlan/payment/verifySetup/${idForPolling}`,
    { params: { gateway: 'PhonePe' } }
  );
  return response.data;
};

export const cancelPhonePeSubscription = async () => {
  const response = await api.post<CancelPhonePeSubscriptionResponse>(
    'premiumPlan/payment/cancelSubscription',
    { gateway: 'PhonePe' }
  );
  return response.data;
};

export const verifyCashfreeSubscription = async (subscriptionId: string) => {
  const response = await api.get<VerifyAuthPaymentResponse>(
    `premiumPlan/cashfree/verifyAuthPayment/${subscriptionId}`
  );
  return response.data;
};

// Coupon types
export interface ValidateCouponResponse {
  status: boolean;
  valid: boolean;
  message: string;
  code?: string;
  source?: string;
  campaign?: string;
  modified_plan?: {
    trial_days: number;
    price: number;
    duration: number;
  };
}

export interface ApplyCouponResponse {
  status: boolean;
  message: string;
  premiumplanId?: string;
  source?: string;
  campaign?: string;
  override?: {
    price: number;
    duration: number;
    validityType: string;
    trial_days: number;
    cycles: number;
  };
  code?: string;
  valid?: boolean;
}

export interface CancelCouponResponse {
  status: boolean;
  message: string;
  coupon?: any;
  code?: string;
}

export const validateCoupon = async (couponCode: string): Promise<ValidateCouponResponse> => {
  const { data } = await api.post<ValidateCouponResponse>('/coupon/validate', { couponCode });
  return data;
};

export const applyCoupon = async (couponCode: string): Promise<ApplyCouponResponse> => {
  const { data } = await api.post<ApplyCouponResponse>('/coupon/apply', { couponCode });
  return data;
};

export const cancelCoupon = async (couponCode: string): Promise<CancelCouponResponse> => {
  const { data } = await api.post<CancelCouponResponse>('/coupon/cancel', { couponCode });
  return data;
};

export default api;
